import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { fieldSorter } from '@scandium-oy/ngx-scandium';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { BehaviorSubject, combineLatest, map, of, switchMap } from 'rxjs';
import { auditTime, filter, shareReplay } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { MonthSwitchComponent } from 'src/app/components/month-switch/month-switch.component';
import { DashboardViewComponent } from 'src/app/components/views/dashboard/view-dashboard.component';
import { AiUsage, AiUsageService } from 'src/app/services/ai-usage.service';
import { ContractorsService } from 'src/app/services/contractors.service';
import { UserDirectoryService } from 'src/app/services/user-directory.service';
import { baseMaxTokens } from 'src/app/utility/constants';

@Component({
  selector: 'app-super-admin-ai-usage-page',
  templateUrl: './ai-usage.page.html',
  styleUrls: ['./ai-usage.page.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    DashboardViewComponent,
    MonthSwitchComponent,
  ],
})
export class SuperAdminAiUsagePage implements OnInit {
  private aiUsageService = inject(AiUsageService);
  private contractorsService = inject(ContractorsService);
  private usersService = inject(UserDirectoryService);

  startDate$ = new BehaviorSubject<Date>(null);
  endDate$ = new BehaviorSubject<Date>(null);

  private dateChange$ = combineLatest([this.startDate$, this.endDate$]).pipe(
    filter(([startDate, endDate]) => !!startDate && !!endDate),
    filter(([startDate, endDate]) => startDate <= endDate),
    auditTime(100),
  );

  usages$ = this.dateChange$.pipe(
    switchMap(([startDate, _endDate]) => combineLatest([of(startDate), this.aiUsageService.getList(), this.contractorsService.getList()])),
    map(([date, usages, contractors]) => {
      const current = format(date, 'MM/yy');
      return usages.map((it) => {
        const maxTokens$ = this.usersService.getActiveCount(it.guid).pipe(
          map((licenses) => ({
            input: baseMaxTokens.input * licenses,
            output: baseMaxTokens.output * licenses,
          })),
          shareReplay({ bufferSize: 1, refCount: true }),
        );
        const usage = it[current] as AiUsage;
        const contractor = contractors.find((c) => c.guid === it.guid);
        return { guid: it.guid, name: contractor?.name ?? it.guid, contractor, usage, maxTokens$ };
      });
    }),
    map((items) => items.sort(fieldSorter(['name']))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  ngOnInit(): void {
    this.changeMonth(new Date());
  }

  changeMonth(newMonth: Date) {
    const startDate = startOfMonth(newMonth);
    const endDate = endOfMonth(startDate);
    this.startDate$.next(startDate);
    this.endDate$.next(endDate);
  }
}
